import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';

import { createStore } from '@/common/pinia';
import { useLayerCustomizationStore } from '@/views/dashboard/zone/details/layers/layer-customization.store';
import type { CognitoResponse } from './auth.types';

const REFRESH_THRESHOLD = 10 * 60;
const USER_POOL = new AmazonCognitoIdentity.CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_POOL_ID,
  ClientId: import.meta.env.VITE_COGNITO_POOL_CLIENT_ID,
});

class AuthStore {
  private cachedToken: string | null = null;
  private tokenExpiry: number | null = null;
  private refreshPromise: Promise<string> | null = null;
  public user: AmazonCognitoIdentity.CognitoUser | null = null;
  public email: string | null = null;
  public temporary: string | null = null;
  public userId: string | null = null;
  public userGroups: string[] = [];
  /** Parsed `access` claim from the ID token, e.g. { layer: "*;-x", tool: "a;b" }. */
  public access: Record<string, string> | null = null;

  public setEmail(email: AuthStore['email'] = null): void {
    this.email = email;
  }

  /**
   * The claims the UI gates on, from one accepted ID token: the Cognito groups
   * and the `access` claim. Every sign-in and refresh path goes through here,
   * so the two never come from different tokens (an admin signed in by
   * password used to keep no groups until a reload).
   */
  private setAccessClaim(idToken: AmazonCognitoIdentity.CognitoIdToken): void {
    this.userGroups = idToken.payload['cognito:groups'] ?? [];
    try {
      const raw = idToken.payload['access'];
      this.access = typeof raw === 'string' ? JSON.parse(raw) : null;
    } catch {
      this.access = null;
    }
  }

  /**
   * Resolve tool visibility from the `access` claim's `tool` entry.
   * Semantics (must match the pre-token lambda): `*` = all tools,
   * `-id` entries are exclusions, otherwise a plain allow-list.
   * A missing claim or `tool` key means allow all (backwards compatible).
   */
  /**
   * Parsed entries of the `access` claim's `tool` spec. The grammar is a
   * contract with the pre-token Lambda, so it is parsed in exactly one place;
   * callers that need only part of it (see `isToolExcluded`) go through here
   * rather than re-splitting the string.
   */
  private accessToolEntries(): string[] {
    const spec = this.access?.['tool'];

    return typeof spec === 'string' ? spec.split(';').map((entry) => entry.trim()).filter(Boolean) : [];
  }

  /** True only for an explicit `-<toolId>` exclusion; an absent claim is not an exclusion. */
  public isToolExcluded(toolId: string): boolean {
    return this.accessToolEntries().includes(`-${toolId}`);
  }

  public canUseTool(toolId: string): boolean {
    const entries = this.accessToolEntries();

    // No entries means the claim is absent, or present but empty. Neither
    // carries information, so both allow everything — the documented contract
    // is that a missing claim or `tool` key means allow all. Treating an empty
    // string as deny-all would let a pre-token trigger that emits `""` for a
    // user with no `viewTools` rows silently hide every tool in every zone,
    // with no error: the toolbar groups collapse when no member is permitted.
    if (entries.length === 0) {
      return true;
    }

    if (entries.includes(`-${toolId}`)) {
      return false;
    }

    return entries.includes('*') || entries.includes(toolId);
  }

  /**
   * Whether notes, disturbances and Documents are available. `access.notes`
   * is the only control (contract 261026_notes_permissions): "none" turns
   * them off, admins always have them, and the `tool` entry plays no part.
   * UI only; the notes API applies the full rule.
   */
  public canUseNotes(): boolean {
    return this.isAdmin() || this.access?.['notes'] !== 'none';
  }

  /** Admin = the `admins` Cognito group. Gates admin-only UI (e.g. Draw → Promote); the backend re-enforces it. */
  public isAdmin(): boolean {
    return this.userGroups.includes('admins');
  }

  public setTemporaryPassword(temporary: AuthStore['temporary'] = null): void {
    this.temporary = temporary;
  }

  public async getCognitoToken(): Promise<string> {
    if (this.isTokenValid()) {
      return this.cachedToken!;
    }

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = new Promise<string>((resolve, reject) => {
      const cognitoUser = USER_POOL.getCurrentUser();

      if (!cognitoUser) {
        this.clearCache();
        history.pushState(history.state, '', '/auth/login?error=no-user');
        reject(new Error('No user found'));
        return;
      }

      cognitoUser.getSession(
        async (err: Error | null, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
          if (err || !session) {
            this.clearCache();
            history.pushState(history.state, '', '/auth/login?error=no-session');
            reject(new Error('No session found'));
            return;
          }

          const idToken = session.getIdToken();
          const token = idToken.getJwtToken();
          const expiry = idToken.getExpiration();

          if (this.shouldRefreshToken(expiry)) {
            try {
              const newToken = await this.refreshSession(cognitoUser, session);
              resolve(newToken);
            } catch (error) {
              this.clearCache();
              history.pushState(history.state, '', '/auth/login?error=refresh-failed');
              reject(error);
            }
          } else {
            this.userId = cognitoUser.getUsername();
            this.cachedToken = token;
            this.tokenExpiry = expiry;
            this.setAccessClaim(idToken);
            useLayerCustomizationStore().load(this.userId);
            resolve(token);
          }
        },
      );
    });

    try {
      const token = await this.refreshPromise;

      return token;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async refreshSession(
    cognitoUser: AmazonCognitoIdentity.CognitoUser,
    session: AmazonCognitoIdentity.CognitoUserSession,
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const refreshToken = session.getRefreshToken();
      cognitoUser.refreshSession(refreshToken, (err, newSession) => {
        if (err || !newSession) {
          reject(new Error('Failed to refresh session'));
          return;
        }

        const idToken = newSession.getIdToken();

        this.userId = cognitoUser.getUsername();
        this.cachedToken = idToken.getJwtToken();
        this.tokenExpiry = idToken.getExpiration();
        this.setAccessClaim(idToken);
        this.user = cognitoUser;
        useLayerCustomizationStore().load(this.userId);

        if (this.cachedToken) {
          resolve(this.cachedToken);
        } else {
          reject(new Error('Failed to refresh session'));
        }
      });
    });
  }

  private isTokenValid(): boolean {
    if (!this.cachedToken || !this.tokenExpiry) {
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const secondsRemaining = this.tokenExpiry - currentTime;

    return secondsRemaining > REFRESH_THRESHOLD;
  }

  private shouldRefreshToken(expiry: number): boolean {
    const currentTime = Math.floor(Date.now() / 1000);

    return currentTime >= expiry - REFRESH_THRESHOLD;
  }

  private clearCache(): void {
    this.cachedToken = null;
    this.tokenExpiry = null;
    this.user = null;
    this.access = null;
    this.userGroups = [];
  }

  public async autoLogin(): Promise<boolean> {
    const currentUser = USER_POOL.getCurrentUser();

    if (!currentUser) {
      return false;
    }

    return new Promise((resolve) => {
      currentUser.getSession(
        async (err: Error | null, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
          if (err || !session) {
            console.log({ err });
            this.clearCache();
            resolve(false);
            return;
          }

          if (!session.isValid()) {
            this.clearCache();
            resolve(false);
            return;
          }

          try {
            await this.refreshSession(currentUser, session);

            resolve(true);
          } catch (error) {
            console.log({ error });
            this.clearCache();
            resolve(false);
          }
        },
      );
    });
  }

  public async loginByEmailAndPassword(email: string, password: string) {
    const authData = { Password: password, Username: email };
    const userData = { Pool: USER_POOL, Username: email };
    const authDetails = new AmazonCognitoIdentity.AuthenticationDetails(authData);
    const user = new AmazonCognitoIdentity.CognitoUser(userData);

    return new Promise<CognitoResponse>((resolve) => {
      user.authenticateUser(authDetails, {
        newPasswordRequired: () => {
          resolve({ error: 'SET_NEW_PASSWORD' });
        },
        onFailure: (error: Error) => {
          console.log('onFailure', { error });
          resolve({ error: error.message });
        },
        onSuccess: async (session) => {
          this.user = user;
          this.cachedToken = session.getIdToken().getJwtToken();
          this.tokenExpiry = session.getIdToken().getExpiration();
          this.setAccessClaim(session.getIdToken());

          resolve({});
        },
      });
    });
  }

  public changePassword(currentPassword: string, newPassword: string) {
    return new Promise<CognitoResponse>((resolve) => {
      const cognitoUser = USER_POOL.getCurrentUser();

      if (cognitoUser === null) {
        resolve({ error: 'No user' });
      } else {
        cognitoUser.getSession(
          (err: Error | null, session: AmazonCognitoIdentity.CognitoUserSession | null) => {
            if (err !== null || session === null) {
              resolve({ error: 'No session' });
            } else {
              cognitoUser.changePassword(currentPassword, newPassword, (error) => {
                if (error) {
                  resolve({ error: error.message });
                } else {
                  resolve({});
                }
              });
            }
          },
        );
      }
    });
  }

  public completeNewPasswordChallenge(newPassword: string) {
    if (!this.email) {
      return { error: 'no-email' };
    }

    if (!this.temporary) {
      return { error: 'no-temporary-password' };
    }

    return new Promise<CognitoResponse>((resolve) => {
      const authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails({
        Username: this.email as string,
        Password: this.temporary as string,
      });

      const userData = { Pool: USER_POOL, Username: this.email as string };
      const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session) => {
          this.setTemporaryPassword();

          this.user = cognitoUser;
          this.cachedToken = session.getIdToken().getJwtToken();
          this.tokenExpiry = session.getIdToken().getExpiration();
          this.setAccessClaim(session.getIdToken());

          resolve({});
        },
        onFailure: (error) => {
          resolve({ error: error.message });
        },
        newPasswordRequired: () => {
          cognitoUser.completeNewPasswordChallenge(
            newPassword,
            {},
            {
              onSuccess: (session) => {
                this.setTemporaryPassword();

                this.user = cognitoUser;
                this.cachedToken = session.getIdToken().getJwtToken();
                this.tokenExpiry = session.getIdToken().getExpiration();
                this.setAccessClaim(session.getIdToken());
                resolve({});
              },
              onFailure: (error) => {
                resolve({ error: error.message });
              },
            },
          );
        },
      });
    });
  }

  public requestPasswordReset(email: string) {
    return new Promise<CognitoResponse>((resolve) => {
      const userData = { Pool: USER_POOL, Username: email };
      const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

      cognitoUser.forgotPassword({
        onFailure: (error) => {
          console.log({ error });
          resolve({ error: error.message });
        },
        onSuccess: () => {
          resolve({});
        },
      });
    });
  }

  public completePasswordReset(email: string, otp: string, newPassword: string) {
    return new Promise<CognitoResponse>((resolve) => {
      const userData = { Pool: USER_POOL, Username: email };
      const cognitoUser = new AmazonCognitoIdentity.CognitoUser(userData);

      cognitoUser.confirmPassword(otp, newPassword, {
        onFailure: (error) => {
          console.log({ error });
          resolve({ error: error.message });
        },
        onSuccess: () => {
          resolve({});
        },
      });
    });
  }

  public signOut(): void {
    const cognitoUser = USER_POOL.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.signOut();
    }

    this.userId = null;
    useLayerCustomizationStore().load(null);
    this.clearCache();
  }
}

const useAuthStore = createStore(AuthStore);

export { useAuthStore, type AuthStore };
